/**
 * One browser profile, one Compass user's cached data.
 *
 * Pages cache API results in localStorage (e.g. the Overview's
 * `dashboard:results:v1`) and render them before the fresh fetch lands. On a
 * shared browser — or the Compass App's WebView, whose storage outlives a
 * sign-out — the next person to sign in would briefly see the previous
 * person's numbers. In particular, an App Review account signing in after an
 * employee must never see the employee's cached data.
 *
 * `claimStorageFor(userId)` runs when /auth/me resolves, before any page
 * renders: if the storage belongs to someone else, every key except plain UI
 * preferences is removed and the storage is stamped with the new owner.
 */

export const STORAGE_OWNER_KEY = 'compass:storage-owner';

/** UI preferences that carry no data and may survive a change of user. */
const KEEP_KEYS: ReadonlySet<string> = new Set([
  'compass-theme',
  'compass:embedded',
  'compass:stale-chunk-reload-at',
  'compass.accountChat.width',
  'compass.dashboardChat.width',
]);

/** Returns true when the storage changed hands (and was cleared). */
export function claimStorageFor(userId: string, storage: Storage | undefined = safeLocalStorage()): boolean {
  if (!storage) return false;
  try {
    const owner = storage.getItem(STORAGE_OWNER_KEY);
    if (owner === userId) return false;
    // First visit after this shipped (owner unknown) is treated like a change of
    // user: the cached entries cannot be attributed to anyone.
    const keys: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const k = storage.key(i);
      if (k !== null && !KEEP_KEYS.has(k)) keys.push(k);
    }
    for (const k of keys) storage.removeItem(k);
    storage.setItem(STORAGE_OWNER_KEY, userId);
    return true;
  } catch {
    return false;
  }
}

function safeLocalStorage(): Storage | undefined {
  try {
    return typeof window !== 'undefined' ? window.localStorage : undefined;
  } catch {
    return undefined;
  }
}
